import React from 'react';
import PropTypes from 'prop-types';
import Paper from 'components/Paper';
import styled from 'styled-components';
import moment from 'moment';
import Button from 'components/Button';
import UsageAndPrice from 'components/UsageAndPrice';
import { useHistory } from 'react-router';
import ProductImageView from 'components/ProductImageView';
import NextPaymentDifferent from 'components/infoBoxes/NextPaymentDifferent';
import WillSwitchUsage from 'components/infoBoxes/WillSwitchUsage';
import topupsContainerPath from 'containers/paths/OrderTopupsContainerPath';
import changeLensAmountContainerPath from 'containers/paths/ChangeLensAmountContainerPath';

function formatDeliverDate(expectedDelivery) {
  return moment(expectedDelivery).format('D MMM');
}

const YourPackagePaper = styled(Paper)`
  margin-bottom: ${({ theme }) => theme.spacing * 6}px;
  .expected_delivery {
    font-size: 1.2rem;
    color: ${({ theme }) => theme.palette.text.light};
    margin-bottom: ${({ theme }) => theme.spacing * 2}px;
  }
  ${Button} {
    margin-top: ${({ theme }) => theme.spacing * 2}px;
  }

`;

const YourPackageWidget = (
  {
    pack,
    topupsDisabledReason,
    changeUsageDisableReason,
    nextPaymentAmount,
    hideChangeUsage,
  },
) => {
  const { product } = pack;
  const payment = pack.payments.find((p) => p.amount > 0);
  const price = {
    price: payment.amount,
    currency: payment.currency,
    frequency: 'mo.',
  };

  const history = useHistory();
  function onChangeLensAmount() {
    history.push(changeLensAmountContainerPath());
  }

  function onTopup() {
    history.push(topupsContainerPath());
  }

  /**
   * Renders an infoBox if necessary.
   * Only one info box will be returned, therefore, if there are packChanges, the outstanding
   * amount message will not be displayed
   *
   * @returns {null|*}
   */
  const renderInfoBox = () => {
    if (pack && pack.packChanges) {
      return (
        <WillSwitchUsage
          currentProduct={pack.product}
          newProduct={pack.packChanges.product}
          deliveryDate={pack.deliveryDate}
        />
      );
    }

    if (nextPaymentAmount && nextPaymentAmount !== price.price) {
      return (
        <NextPaymentDifferent currency={price.currency} nextPaymentAmount={nextPaymentAmount} />
      );
    }

    return null;
  };

  return (
    <>
      <YourPackagePaper className="YourPackageWidget">
        <h4>Your next delivery</h4>
        <UsageAndPrice usage={product.packSize.usage} price={price} />
        {pack && pack.deliveryDate && moment(pack.deliveryDate).isAfter() && (
          <div className="expected_delivery">
            Delivery expected:&nbsp;
            {formatDeliverDate(pack.deliveryDate)}
          </div>
        )}

        <ProductImageView format="largeImage" items={product.items} />

        {renderInfoBox()}

        <Button
          fullWidth
          disableReason={topupsDisabledReason}
          onClick={onTopup}
          dataLayer={{ action: 'Top Up', label: 'Top Up - Start' }}
        >
          Order top-ups
        </Button>
        { !hideChangeUsage && (
          <Button
            fullWidth
            inverse
            disableReason={changeUsageDisableReason}
            // TODO: we need to update GTM as well
            dataLayer={{ action: 'Change Lens Amount', label: 'Change Lens Amount - Start' }}
            onClick={onChangeLensAmount}
          >
            Change package
          </Button>
        )}
      </YourPackagePaper>
    </>
  );
};

YourPackageWidget.displayName = 'YourPackageWidget';

YourPackageWidget.propTypes = {
  pack: PropTypes.shape({
    product: PropTypes.shape({
      items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
      packSize: PropTypes.shape({
        usage: PropTypes.string,
      }),
    }),
    payments: PropTypes.arrayOf(PropTypes.shape),
    deliveryDate: PropTypes.string,
    packChanges: PropTypes.shape({
      product: PropTypes.shape({
        packSize: PropTypes.shape({
          boxSize: PropTypes.number.isRequired,
          usage: PropTypes.string.isRequired,
        }).isRequired,
      }),
    }),
  }).isRequired,
  nextPaymentAmount: PropTypes.number,
  topupsDisabledReason: PropTypes.node,
  changeUsageDisableReason: PropTypes.string,
  hideChangeUsage: PropTypes.bool,
};

YourPackageWidget.defaultProps = {
  topupsDisabledReason: null,
  changeUsageDisableReason: null,
  nextPaymentAmount: null,
  hideChangeUsage: false,
};

export default YourPackageWidget;
